import React, { useState, useEffect } from "react";
import { ChevronRight, ChevronDown, Plus, X, Boxes, Package, Search } from "lucide-react";
import api from "@/lib/api";
import { LaborHoursInput } from "@/lib/laborUnit";
import { QtyInput } from "@/lib/qtyInput";
import { fuzzyIncludes } from "@/lib/fuzzy";

// Roll a component list up to man-hours + material. (Multiplier & waste are no longer edited in the
// assembly UI — waste lives on the estimating page and sizing is done via quantity — but the math
// still reads them so any legacy values default harmlessly to multiplier=1 / waste=0.)
export function rollupComponents(components) {
  let mh = 0, mat = 0;
  for (const c of components || []) {
    const f = (Number(c.qty) || 0) * (Number(c.multiplier) || 1);
    const waste = 1 + (Number(c.waste_pct) || 0) / 100;
    if (c.kind === "assembly") {
      const sub = rollupComponents(c.children || []);
      const per = Number(c.per_unit) || 1;
      const nmh = f * (sub.mh / per);
      const nmat = f * waste * (sub.mat / per);
      c._roll = { mh: nmh, material: nmat };
      mh += nmh; mat += nmat;
    } else {
      const em = Number(c.material_effective ?? c.material_cost) || 0;
      const nmh = f * (Number(c.man_hours) || 0);
      const nmat = f * waste * em;
      c._roll = { mh: nmh, material: nmat };
      mh += nmh; mat += nmat;
    }
  }
  return { mh, mat };
}

// Per-unit man-hours & material for a whole resolved tree (what a takeoff layer freezes).
export function treePerUnit(tree) {
  const { mh, mat } = rollupComponents(tree?.components || []);
  const per = Number(tree?.per_unit) || 1;
  return { mh, mat, mh_per_unit: mh / per, material_per_unit: mat / per };
}

// Flatten a component tree into its LEAF parts, carrying each leaf's quantity, man-hours and
// material for ONE "per_unit batch" of the tree (i.e. the components list as written). Nested
// sub-assemblies are expanded; their per_unit + child qtys compound into the leaf quantities.
// Scale the returned per-batch numbers by the layer's resolved assembly count to get absolutes.
export function flattenLeaves(components, qf = 1, wf = 1, out = []) {
  for (const c of components || []) {
    const f = (Number(c.qty) || 0) * (Number(c.multiplier) || 1);
    const waste = 1 + (Number(c.waste_pct) || 0) / 100;
    if (c.kind === "assembly" || c.children) {
      const per = Number(c.per_unit) || 1;
      flattenLeaves(c.children || [], qf * (f / per), wf * waste, out);
    } else {
      const em = Number(c.material_effective ?? c.material_cost) || 0;
      const qty = qf * f;
      out.push({ name: c.name || "Item", brand: c.brand || "", cost_code: c.cost_code || "",
        qty, mh: qty * (Number(c.man_hours) || 0), material: qty * wf * waste * em });
    }
  }
  return out;
}

const money = (v) => `$${(Number(v) || 0).toLocaleString("en-US", { maximumFractionDigits: 2 })}`;
const num = (v, d = 2) => (Number(v) || 0).toLocaleString("en-US", { maximumFractionDigits: d });

// Normalize an edited drill-down tree into component bodies for POST /assemblies. Assembly nodes
// keep their inline children as a self-contained snapshot (no library reference). Shared by the
// takeoff dialogs and the estimating line editor. multiplier/waste are persisted at their defaults.
export function cleanTreeNodes(nodes) {
  return (nodes || []).map((c) => (c.kind === "assembly" || c.children) ? {
    kind: "assembly", name: c.name || "", qty: Number(c.qty) || 0, multiplier: Number(c.multiplier) || 1,
    waste_pct: Number(c.waste_pct) || 0, per_unit: Number(c.per_unit) || 1, children: cleanTreeNodes(c.children),
  } : {
    name: c.name || "", qty: Number(c.qty) || 0, multiplier: Number(c.multiplier) || 1, waste_pct: Number(c.waste_pct) || 0,
    man_hours: Number(c.man_hours) || 0, material_cost: Number(c.material_effective ?? c.material_cost) || 0,
    commodity_index_id: c.commodity_index_id || null, commodity_baseline_price: c.commodity_baseline_price ?? null,
    cost_code: c.cost_code || "", brand: c.brand || "",
  });
}

// Build an editable leaf component from a saved catalog line item, carrying over every field.
export function leafFromCatalog(item, indices = []) {
  const mat = Number(item.unit_material_cost) || 0;
  const idx = (indices || []).find((x) => x.index_id === item.commodity_index_id);
  return {
    kind: "line_item", name: item.name || "", qty: 1, multiplier: 1, waste_pct: 0,
    man_hours: Number(item.unit_labor_hours) || 0, material_cost: mat, material_effective: mat,
    commodity_index_id: item.commodity_index_id || "", commodity_baseline_price: item.commodity_baseline_price ?? null,
    index_name: idx?.name || null, cost_code: item.cost_code || "", brand: item.brand || "", catalog_id: item.catalog_id,
  };
}

// "Add from library" — search your existing catalog line items (and optionally assemblies) and
// insert them with every field carried over. Picking an assembly inlines its resolved component tree.
export function LibraryAdd({ catalog = [], assemblies = [], indices = [], onAdd, label = "Add from library", testid = "lib-add" }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const s = q.trim();
  const items = (s ? catalog.filter((c) => fuzzyIncludes(s, `${c.name || ""} ${c.cost_code || ""} ${c.category || ""} ${c.brand || ""}`)) : catalog);
  const asms = (s ? assemblies.filter((a) => fuzzyIncludes(s, `${a.name || ""} ${a.cost_code || ""} ${a.category || ""}`)) : assemblies);
  const close = () => { setOpen(false); setQ(""); };
  const pickItem = (it) => { onAdd(leafFromCatalog(it, indices)); close(); };
  const pickAsm = async (a) => {
    setBusy(true);
    try {
      const { data } = await api.get(`/assemblies/${a.assembly_id}/resolved`);
      onAdd({ kind: "assembly", name: data.name || a.name || "", qty: 1, multiplier: 1, waste_pct: 0,
        per_unit: Number(data.per_unit) || 1, children: data.components || [], sub_assembly_id: a.assembly_id });
    } catch { /* ignore */ } finally { setBusy(false); close(); }
  };
  return (
    <div className="relative inline-block" data-testid={testid}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`${testid}-toggle`}
        className="text-[11px] font-medium text-[#003E87] hover:bg-[#003E87]/5 rounded px-1.5 py-0.5 flex items-center gap-1">
        <Search className="w-3 h-3" /> {label}
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[2147483630]" onClick={close} data-testid={`${testid}-backdrop`} />
          <div className="absolute left-0 z-[2147483631] mt-1 w-80 max-w-[85vw] bg-white border border-slate-200 rounded-lg shadow-2xl p-2" data-testid={`${testid}-panel`}>
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search your library…" data-testid={`${testid}-search`}
              className="w-full text-xs border border-slate-200 rounded px-2 py-1.5 mb-1 focus:border-[#003E87] focus:ring-2 focus:ring-[#003E87]/20 outline-none" />
            <div className="max-h-64 overflow-auto">
              {asms.length > 0 && <div className="text-[9px] uppercase tracking-wide text-slate-400 px-1 pt-1 pb-0.5">Assemblies</div>}
              {asms.slice(0, 40).map((a) => (
                <button key={a.assembly_id} type="button" disabled={busy} onClick={() => pickAsm(a)} data-testid={`${testid}-asm-${a.assembly_id}`}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 flex items-center gap-2 disabled:opacity-50">
                  <Boxes className="w-3.5 h-3.5 text-[#003E87] shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-xs text-slate-700">{a.name}</span>
                  {a.cost_code && <span className="text-[9px] text-slate-400 font-mono shrink-0">{a.cost_code}</span>}
                </button>
              ))}
              {items.length > 0 && <div className="text-[9px] uppercase tracking-wide text-slate-400 px-1 pt-1 pb-0.5">Line items</div>}
              {items.slice(0, 80).map((it) => (
                <button key={it.catalog_id} type="button" onClick={() => pickItem(it)} data-testid={`${testid}-item-${it.catalog_id}`}
                  className="w-full text-left px-2 py-1.5 rounded hover:bg-slate-50 flex items-center gap-2">
                  <Package className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                  <span className="flex-1 min-w-0 truncate text-xs text-slate-700">{it.name}</span>
                  <span className="text-[9px] text-slate-400 font-mono shrink-0">{num(it.unit_labor_hours, 2)}h · {money(it.unit_material_cost)}</span>
                </button>
              ))}
              {asms.length === 0 && items.length === 0 && <div className="text-xs text-slate-400 text-center py-3">Nothing in your library matches.</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Node({ node, editable, depth, indices = [], catalog = [], assemblies = [], onChange, onRemove }) {
  const Self = Node;  // indirection: self-reference via a local alias (keeps the visual-edits babel plugin from recursing)
  const [open, setOpen] = useState(depth < 1);
  const isAsm = node.kind === "assembly";
  const set = (k, v) => onChange({ ...node, [k]: v });
  const setIndex = (iid) => {
    if (!iid) { onChange({ ...node, commodity_index_id: "", commodity_baseline_price: null, index_name: null }); return; }
    const idx = (indices || []).find((x) => x.index_id === iid);
    onChange({ ...node, commodity_index_id: iid, commodity_baseline_price: Number(idx?.current_price) || Number(node.commodity_baseline_price) || 0, index_name: idx?.name });
  };
  const idkey = node.sub_assembly_id || node.catalog_id || node.name || "node";
  const pad = depth * 14;
  const fieldPad = pad + 30;
  return (
    <div data-testid={`drill-node-${idkey}`}>
      {/* Name row — the name is the dominant element */}
      <div className="flex items-center gap-1.5 pt-1.5 hover:bg-slate-50/60 rounded" style={{ paddingLeft: pad }}>
        {isAsm ? (
          <button type="button" onClick={() => setOpen((o) => !o)} className="text-slate-400 shrink-0" data-testid={`drill-toggle-${idkey}`}>
            {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        ) : <span className="w-4 shrink-0" />}
        {isAsm ? <Boxes className="w-4 h-4 text-[#003E87] shrink-0" /> : <Package className="w-3.5 h-3.5 text-slate-300 shrink-0" />}
        {editable ? (
          <input value={node.name || ""} onChange={(e) => set("name", e.target.value)} placeholder="Component name"
            className="flex-1 min-w-0 text-sm font-medium text-slate-800 border border-slate-200 rounded px-2 py-1 focus:border-[#003E87] focus:ring-1 focus:ring-[#003E87]/25 outline-none"
            data-testid={`drill-name-${idkey}`} />
        ) : (
          <span className="flex-1 min-w-0 truncate text-sm font-medium text-slate-800">
            {node.name || "—"}{isAsm && <span className="text-[9px] uppercase tracking-wide text-[#003E87] ml-1.5 font-semibold">assembly</span>}
            {!isAsm && node.index_name && <span className="text-[10px] text-emerald-700 ml-1.5 font-normal">· {node.index_name}</span>}
          </span>
        )}
        {editable && <button type="button" onClick={onRemove} className="text-slate-300 hover:text-rose-600 shrink-0" title="Remove" data-testid={`drill-remove-${idkey}`}><X className="w-4 h-4" /></button>}
        {!editable && (
          <span className="text-[11px] text-slate-500 font-mono shrink-0 tabular-nums">
            ×{num(node.qty, 2)} · {num(node._roll?.mh, 2)}h · {money(node._roll?.material)}
          </span>
        )}
      </div>

      {/* Compact value fields live BELOW the name so the name reads first */}
      {editable && (
        <div className="flex items-center gap-3 flex-wrap pt-1 pb-0.5" style={{ paddingLeft: fieldPad }} data-testid={`drill-fields-${idkey}`}>
          <label className="flex items-center gap-1 text-[10px]">
            <span className="uppercase tracking-wide text-slate-400">Qty</span>
            <QtyInput value={node.qty} onChange={(v) => set("qty", v)} className="w-16 text-xs border border-slate-200 rounded px-1.5 py-0.5 text-right font-mono focus:border-[#003E87] outline-none" testid={`drill-qty-${idkey}`} />
          </label>
          {!isAsm && (
            <>
              <label className="flex items-center gap-1 text-[10px]">
                <span className="uppercase tracking-wide text-slate-400">Time</span>
                <div className="w-28"><LaborHoursInput value={node.man_hours} onChange={(v) => set("man_hours", v)} defaultUnit="min" testid={`drill-mh-${idkey}`} className="text-xs border border-slate-200 rounded px-1.5 py-0.5 text-right font-mono" /></div>
              </label>
              <label className="flex items-center gap-1 text-[10px]">
                <span className="uppercase tracking-wide text-slate-400">$ / ea</span>
                <input type="number" step="any" value={node.material_cost} onChange={(e) => onChange({ ...node, material_cost: e.target.value, material_effective: e.target.value })} title="Material $ / ea" className="w-20 text-xs border border-slate-200 rounded px-1.5 py-0.5 text-right font-mono focus:border-[#003E87] outline-none" data-testid={`drill-mat-${idkey}`} />
              </label>
            </>
          )}
          {isAsm && <span className="text-[10px] text-slate-400 font-mono tabular-nums">{num(node._roll?.mh, 2)} MH · {money(node._roll?.material)}</span>}
        </div>
      )}

      {editable && !isAsm && (
        <div className="flex items-center gap-1 pb-1.5 text-[10px]" style={{ paddingLeft: fieldPad }} data-testid={`drill-index-row-${idkey}`}>
          <span className="text-slate-400 shrink-0">Commodity index</span>
          <select value={node.commodity_index_id || ""} onChange={(e) => setIndex(e.target.value)} className="text-[10px] border border-slate-200 rounded px-1 py-0.5 bg-white max-w-[15rem]" data-testid={`drill-index-${idkey}`}>
            <option value="">— not linked —</option>
            {(indices || []).map((i) => <option key={i.index_id} value={i.index_id}>{i.name}</option>)}
          </select>
          {node.commodity_index_id && <span className="text-[9px] text-emerald-700 shrink-0">material reprices with this index</span>}
        </div>
      )}

      {isAsm && open && (
        <div className="border-l border-slate-100 ml-2">
          {(node.children || []).map((ch, i) => (
            <Self key={i} node={ch} editable={editable} depth={depth + 1} indices={indices} catalog={catalog} assemblies={assemblies}
              onChange={(nc) => { const children = [...(node.children || [])]; children[i] = nc; onChange({ ...node, children }); }}
              onRemove={() => { const children = (node.children || []).filter((_, j) => j !== i); onChange({ ...node, children }); }} />
          ))}
          {editable && (
            <div className="flex items-center gap-3 py-1" style={{ paddingLeft: (depth + 1) * 14 + 4 }}>
              <button type="button" onClick={() => onChange({ ...node, children: [...(node.children || []), { kind: "line_item", name: "New item", qty: 1, multiplier: 1, waste_pct: 0, man_hours: 0, material_cost: 0 }] })}
                className="text-[10px] text-[#003E87] hover:underline flex items-center gap-1">
                <Plus className="w-3 h-3" /> Add blank
              </button>
              <LibraryAdd catalog={catalog} assemblies={assemblies} indices={indices} testid={`drill-lib-add-${idkey}`}
                onAdd={(nn) => onChange({ ...node, children: [...(node.children || []), nn] })} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Recursive, optionally-editable assembly component tree with live man-hour / material rollups.
export function AssemblyDrilldown({ tree, editable = false, onChange, className = "", indices = [], catalog: catalogProp, assemblies: assembliesProp }) {
  const [lib, setLib] = useState({ catalog: catalogProp || [], assemblies: assembliesProp || [] });
  useEffect(() => {
    if (!editable) return undefined;
    let on = true;
    if (!catalogProp) api.get("/catalog", { params: { type: "line_item", limit: 1000 } }).then((r) => { if (on) setLib((prev) => ({ ...prev, catalog: r.data?.items || (Array.isArray(r.data) ? r.data : []) })); }).catch(() => {});
    if (!assembliesProp) api.get("/assemblies").then((r) => { if (on) setLib((prev) => ({ ...prev, assemblies: r.data || [] })); }).catch(() => {});
    return () => { on = false; };
  }, [editable]); // eslint-disable-line react-hooks/exhaustive-deps
  const catalog = catalogProp || lib.catalog;
  const assemblies = assembliesProp || lib.assemblies;
  if (!tree) return null;
  rollupComponents(tree.components || []);   // refresh _roll for display each render
  const pu = treePerUnit(tree);
  const setComp = (i, nc) => { const components = [...(tree.components || [])]; components[i] = nc; onChange?.({ ...tree, components }); };
  const removeComp = (i) => onChange?.({ ...tree, components: (tree.components || []).filter((_, j) => j !== i) });
  const addLeaf = () => onChange?.({ ...tree, components: [...(tree.components || []), { kind: "line_item", name: "New item", qty: 1, multiplier: 1, waste_pct: 0, man_hours: 0, material_cost: 0 }] });
  return (
    <div className={`text-xs ${className}`} data-testid="assembly-drilldown">
      {editable && (
        <div className="flex items-center gap-2 pb-2 mb-1 border-b border-slate-100">
          <span className="text-[10px] uppercase tracking-wide text-slate-400 shrink-0">Assembly name</span>
          <input value={tree.name || ""} onChange={(e) => onChange?.({ ...tree, name: e.target.value })} placeholder="Name this assembly" className="flex-1 min-w-0 text-sm font-semibold text-slate-800 border border-slate-200 rounded px-2 py-1 focus:border-[#003E87] outline-none" data-testid="drill-assembly-name" />
        </div>
      )}
      <div className="max-h-[42vh] overflow-auto divide-y divide-slate-100 py-1">
        {(tree.components || []).map((c, i) => (
          <Node key={i} node={c} editable={editable} depth={0} indices={indices} catalog={catalog} assemblies={assemblies} onChange={(nc) => setComp(i, nc)} onRemove={() => removeComp(i)} />
        ))}
        {(tree.components || []).length === 0 && <div className="text-slate-400 py-3 text-center">No components.</div>}
      </div>
      {editable && (
        <div className="flex items-center gap-3 mt-1">
          <button type="button" onClick={addLeaf} className="text-[11px] text-[#003E87] hover:underline flex items-center gap-1" data-testid="drill-add-top">
            <Plus className="w-3 h-3" /> Add blank item
          </button>
          <LibraryAdd catalog={catalog} assemblies={(assemblies || []).filter((a) => a.assembly_id !== tree.assembly_id)} indices={indices} testid="drill-lib-add-top"
            onAdd={(nn) => onChange?.({ ...tree, components: [...(tree.components || []), nn] })} />
        </div>
      )}
      <div className="flex items-center justify-between mt-2 pt-2 border-t border-slate-200 text-[11px]">
        <span className="text-slate-400">Per {num(tree.per_unit, 2)} {tree.measure_type === "linear" ? "ft" : tree.measure_type === "area" ? "sf" : "ea"}</span>
        <span className="font-mono font-semibold text-slate-800" data-testid="drill-perunit">
          {num(pu.mh_per_unit, 3)} MH · {money(pu.material_per_unit)} / unit
        </span>
      </div>
    </div>
  );
}
